import React from 'react';
import { Info, HelpCircle, Lightbulb, AlertCircle } from 'lucide-react';
import Tooltip from './Tooltip';

/**
 * TooltipExamples - ตัวอย่างการใช้งาน Tooltip component
 * ไฟล์นี้แสดงวิธีการใช้งาน Tooltip ในรูปแบบต่างๆ
 */
const TooltipExamples = () => {
  return (
    <div className="max-w-4xl mx-auto p-8 space-y-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          ตัวอย่างการใช้งาน Tooltip
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          วางเมาส์เหนือ elements ต่างๆ เพื่อดู tooltips
        </p>
      </div>

      {/* Example 1: Basic Tooltip */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          1. Tooltip พื้นฐาน
        </h2>
        <div className="flex flex-wrap gap-4">
          <Tooltip content="นี่คือ tooltip ด้านบน" position="top">
            <button className="btn-primary">Top Tooltip</button>
          </Tooltip>

          <Tooltip content="นี่คือ tooltip ด้านล่าง" position="bottom">
            <button className="btn-primary">Bottom Tooltip</button>
          </Tooltip>

          <Tooltip content="นี่คือ tooltip ด้านซ้าย" position="left">
            <button className="btn-primary">Left Tooltip</button>
          </Tooltip>

          <Tooltip content="นี่คือ tooltip ด้านขวา" position="right">
            <button className="btn-primary">Right Tooltip</button>
          </Tooltip>
        </div>
      </div>

      {/* Example 2: Tooltip with Title */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          2. Tooltip พร้อมหัวข้อ
        </h2>
        <div className="flex gap-4">
          <Tooltip 
            title="ข้อมูลสำคัญ" 
            content="คุณสามารถเพิ่มหัวข้อให้กับ tooltip เพื่อให้ข้อมูลชัดเจนยิ่งขึ้น"
            position="bottom"
          >
            <button className="btn-primary">
              <Info className="h-4 w-4 inline mr-2" />
              Tooltip พร้อมหัวข้อ
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Example 3: Tooltip with Icon */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          3. Tooltip พร้อมไอคอนแจ้งเตือน
        </h2>
        <div className="flex gap-4">
          <Tooltip 
            content="ไอคอนนี้จะแสดงเพื่อบอกว่ามีข้อมูลเพิ่มเติม"
            showIcon={true}
            position="bottom"
          >
            <div className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg text-gray-900 dark:text-gray-100">
              คลิกที่ไอคอนข้างบนเพื่อดูข้อมูลเพิ่มเติม
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Example 4: Persistent Tooltip */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          4. Tooltip ที่แสดงตลอดเวลา
        </h2>
        <div className="flex gap-4">
          <Tooltip 
            title="คำแนะนำสำหรับผู้ใช้ใหม่"
            content="Tooltip แบบนี้จะแสดงตลอดเวลาจนกว่าจะถูกปิด เหมาะสำหรับคำแนะนำที่สำคัญ"
            persistent={true}
            position="right"
            showIcon={true}
          >
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg text-gray-900 dark:text-gray-100">
              ฟีเจอร์ใหม่! วางเมาส์เพื่อดูรายละเอียด
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Example 5: Tooltip on Form Fields */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          5. Tooltip บน Form Fields
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              ชื่อผู้ใช้
              <Tooltip 
                content="ชื่อผู้ใช้ต้องมีความยาว 3-20 ตัวอักษร และใช้ได้เฉพาะตัวอักษร ตัวเลข และขีดล่าง"
                position="right"
              >
                <HelpCircle className="h-4 w-4 inline ml-2 text-gray-400 cursor-help" />
              </Tooltip>
            </label>
            <input 
              type="text" 
              className="input-field" 
              placeholder="กรอกชื่อผู้ใช้"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              รหัสผ่าน
              <Tooltip 
                title="ความปลอดภัยของรหัสผ่าน"
                content="รหัสผ่านควรมีความยาวอย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวพิมพ์เล็ก พิมพ์ใหญ่ ตัวเลข และอักขระพิเศษ"
                position="right"
              >
                <HelpCircle className="h-4 w-4 inline ml-2 text-gray-400 cursor-help" />
              </Tooltip>
            </label>
            <input 
              type="password" 
              className="input-field" 
              placeholder="กรอกรหัสผ่าน"
            />
          </div>
        </div>
      </div>

      {/* Example 6: Tooltip on Cards/Stats */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          6. Tooltip บนการ์ดสถิติ
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Tooltip 
            title="ลูกค้าทั้งหมด"
            content="จำนวนลูกค้าทั้งหมดในระบบ รวมลูกค้าที่ active และ inactive"
            position="bottom"
          >
            <div className="bg-blue-50 dark:bg-blue-900 p-6 rounded-lg cursor-help hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">ลูกค้าทั้งหมด</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">1,234</p>
                </div>
                <Info className="h-8 w-8 text-blue-400" />
              </div>
            </div>
          </Tooltip>

          <Tooltip 
            title="โอกาสทางการขาย"
            content="โอกาสทางการขายที่กำลังดำเนินการอยู่ ยังไม่ปิดการขาย"
            position="bottom"
          >
            <div className="bg-green-50 dark:bg-green-900 p-6 rounded-lg cursor-help hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">โอกาสทางการขาย</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">567</p>
                </div>
                <Lightbulb className="h-8 w-8 text-green-400" />
              </div>
            </div>
          </Tooltip>

          <Tooltip 
            title="ยอดขายรวม"
            content="ยอดขายรวมในเดือนนี้ เทียบกับเดือนที่แล้วเพิ่มขึ้น 15%"
            position="bottom"
          >
            <div className="bg-purple-50 dark:bg-purple-900 p-6 rounded-lg cursor-help hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">ยอดขายรวม</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">฿89M</p>
                </div>
                <AlertCircle className="h-8 w-8 text-purple-400" />
              </div>
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Usage Guide */}
      <div className="card bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          📖 วิธีใช้งาน Tooltip Component
        </h2>
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <div>
            <strong>1. Import component:</strong>
            <pre className="mt-2 p-3 bg-gray-800 text-gray-100 rounded overflow-x-auto">
              {`import Tooltip from './components/Tutorial/Tooltip';`}
            </pre>
          </div>
          
          <div>
            <strong>2. ใช้งานพื้นฐาน:</strong>
            <pre className="mt-2 p-3 bg-gray-800 text-gray-100 rounded overflow-x-auto">
{`<Tooltip content="ข้อความที่ต้องการแสดง" position="top">
  <button>ปุ่มที่มี tooltip</button>
</Tooltip>`}
            </pre>
          </div>
          
          <div>
            <strong>3. Props ที่ใช้ได้:</strong>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">content</code> - เนื้อหาของ tooltip (required)</li>
              <li><code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">title</code> - หัวข้อของ tooltip (optional)</li>
              <li><code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">position</code> - ตำแหน่ง: 'top', 'bottom', 'left', 'right' (default: 'top')</li>
              <li><code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">showIcon</code> - แสดงไอคอนแจ้งเตือน (default: false)</li>
              <li><code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">persistent</code> - แสดงตลอดเวลา (default: false)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TooltipExamples;

